import { Link, NavLink as RouterNavLink, Outlet, useLocation, useNavigate } from 'react-router';
import {
  Avatar,
  BellIcon,
  Button,
  CarIcon,
  EcoHostingNote,
  IconButton,
  MessageIcon,
  OpenRoadGlyph,
  OpenRoadGlyphCompact,
  SearchIcon,
  SmolitFab,
  SmolitMark,
  TabBar,
  cn,
} from '@openroad/ui';
import { useSession } from '../lib/session';
import { useIsMobileLayout } from '../lib/viewport';
import { SmolitProvider, useSmolit } from '../lib/useSmolit';
import { SmolitPanel } from '../screens/smolit/SmolitPanel';

const NAV = [
  { to: '/search', label: 'Find a ride' },
  { to: '/publish', label: 'Publish a ride' },
  { to: '/trips', label: 'Trips' },
  { to: '/messages', label: 'Messages' },
] as const;

/** The sticky header, reproduced from the export. */
function Header() {
  const { member } = useSession();
  const navigate = useNavigate();
  const smolit = useSmolit();
  const mobile = useIsMobileLayout();

  return (
    // `pt-safe` so the status bar on a phone does not sit on top of the logo.
    // It resolves to zero everywhere else, including in a desktop browser.
    <header className="sticky top-0 z-40 border-b border-line bg-sand/85 pt-safe backdrop-blur-md">
      <div className="mx-auto flex h-16 max-w-[1280px] items-center gap-7 px-6 max-md:gap-3">
        <Link to="/" className="mr-1.5 flex items-center gap-[9px] no-underline max-md:mr-0">
          <span className="flex h-[30px] w-[30px] items-center justify-center rounded-[9px] bg-ngahere">
            <OpenRoadGlyph size={18} />
          </span>
          <span className="font-display text-title font-bold tracking-display text-ink">
            OpenRoad
          </span>
        </Link>

        {/* The navigation and the Smolit button move to the bottom tab bar on a
            phone, where a thumb can reach them (ADR-0010).

            Not rendered and hidden — genuinely not rendered. Two elements
            labelled "Main" would be two navigation landmarks, and a screen
            reader offering "Find a ride" twice on a phone is the sort of thing
            `display: none` happens to fix and nothing then guards. */}
        {mobile ? (
          <span className="flex-1" />
        ) : (
          <nav className="flex flex-1 items-center gap-1" aria-label="Main">
            {NAV.map((item) => (
              <RouterNavLink
                key={item.to}
                to={item.to}
                // Exact match. Without it "Trips" — your dashboard — lights up
                // while you are reading a ride at /trips/:id, which is a page
                // the navigation does not point at.
                end
                className={({ isActive }) =>
                  cn(
                    'cursor-pointer rounded-md px-[13px] py-2 text-body no-underline',
                    'transition-colors duration-fast ease-standard',
                    isActive
                      ? 'bg-ngahere-tint font-bold text-ngahere'
                      : 'font-medium text-ink-2 hover:bg-hover',
                  )
                }
              >
                {item.label}
              </RouterNavLink>
            ))}
          </nav>
        )}

        {!mobile ? (
          <Button
            variant="tealSoft"
            size="sm"
            className="rounded-full pl-2"
            leadingIcon={<SmolitMark size={24} />}
            // Smolit is a dock over whatever screen you are on, not a screen of
            // its own — the export has no Smolit page, and asking about a ride
            // should not navigate away from the ride.
            onClick={smolit.toggle}
          >
            Smolit
          </Button>
        ) : null}

        <IconButton label="Notifications" onClick={() => navigate('/notifications')}>
          <BellIcon size={18} />
          <span className="absolute top-[7px] right-[8px] h-[7px] w-[7px] rounded-full border-[1.5px] border-white bg-pohutukawa" />
        </IconButton>

        {member ? (
          <Link to="/profile" className="flex items-center pl-1.5 no-underline">
            <Avatar
              initials={member.initials}
              colour={member.avatarColour}
              size={34}
              name={member.displayName}
            />
          </Link>
        ) : (
          <Button variant="primary" size="sm" onClick={() => navigate('/sign-in')}>
            Sign in
          </Button>
        )}
      </div>
    </header>
  );
}

/**
 * The bottom tabs, on a phone only.
 *
 * Four, not five: the export's header carries the profile on an avatar and the
 * notifications on a bell, and both of those stay in the mobile header. A fifth
 * "Profile" tab would need a person glyph the export does not contain, which
 * ADR-0010 does not allow — so the four are the four the export's own
 * navigation names, minus "Publish a ride", which has no screen yet, plus
 * Smolit, which on desktop is a button in the same row.
 */
function MobileTabs() {
  const navigate = useNavigate();
  const location = useLocation();
  const smolit = useSmolit();

  const path = location.pathname;

  // `/trips` is exact and `/messages` is not, and the difference is real: a
  // conversation at `/messages/:id` is the screen the Messages tab points at —
  // on a phone the list and the thread take turns behind one destination — but
  // a ride at `/trips/:id` is not the trips dashboard. Lighting "Trips" while
  // somebody reads a ride is the same wrong answer the header's `end` avoids,
  // and it says so there.
  const activeKey = smolit.open
    ? 'smolit'
    : path.startsWith('/messages')
      ? '/messages'
      : path === '/trips'
        ? '/trips'
        : path.startsWith('/search')
          ? '/search'
          : '';

  return (
    <TabBar
      activeKey={activeKey}
      onSelect={(key) => {
        if (key === 'smolit') {
          smolit.toggle();
          return;
        }
        smolit.closePanel();
        navigate(key);
      }}
      items={[
        { key: '/search', label: 'Search', icon: <SearchIcon size={21} /> },
        { key: '/trips', label: 'Trips', icon: <CarIcon size={21} /> },
        { key: 'smolit', label: 'Smolit', icon: <SmolitMark size={22} /> },
        { key: '/messages', label: 'Messages', icon: <MessageIcon size={21} /> },
      ]}
    />
  );
}

const FOOTER_COLUMNS = [
  {
    heading: 'Travel',
    links: [
      { label: 'Find a ride', to: '/search' },
      { label: 'Publish a ride', to: '/publish' },
      { label: 'Popular routes', to: '/popular-routes' },
    ],
  },
  {
    heading: 'Trust',
    links: [
      { label: 'How verification works', to: '/verification' },
      { label: 'Safety', to: '/safety' },
      { label: 'Community guidelines', to: '/guidelines' },
    ],
  },
  {
    heading: 'About',
    links: [
      { label: 'Our impact', to: '/eco' },
      { label: 'somsorium EcoTechFarms', to: '/' },
      { label: 'Contact', to: '/contact' },
    ],
  },
] as const;

function Footer() {
  return (
    <footer className="mx-auto max-w-[1280px] px-6 pt-12 pb-14">
      <div className="flex flex-wrap items-start justify-between gap-6 border-t border-line pt-7">
        <div className="max-w-[280px]">
          <div className="mb-2.5 flex items-center gap-[9px]">
            <span className="flex h-[26px] w-[26px] items-center justify-center rounded-sm bg-ngahere">
              <OpenRoadGlyphCompact size={15} />
            </span>
            <span className="font-display text-lead font-bold">OpenRoad</span>
          </div>
          <p className="m-0 text-label leading-normal text-muted">
            Carpooling for Aotearoa New Zealand. Trust, sustainability, and a great open-road feel.
          </p>
        </div>

        <div className="flex gap-14">
          {FOOTER_COLUMNS.map((column) => (
            <div key={column.heading} className="flex flex-col gap-[9px]">
              <span className="text-caption font-bold tracking-eyebrow text-ink uppercase">
                {column.heading}
              </span>
              {column.links.map((link) => (
                <Link
                  key={link.label}
                  to={link.to}
                  className="text-body-sm text-muted no-underline hover:underline"
                >
                  {link.label}
                </Link>
              ))}
            </div>
          ))}
        </div>
      </div>

      <div className="mt-7 flex items-center justify-between border-t border-line-soft pt-[18px]">
        <span className="text-meta text-subtle">© 2026 OpenRoad · Aotearoa New Zealand</span>
        <EcoHostingNote />
      </div>
    </footer>
  );
}

/**
 * The floating button, and the dock it opens.
 *
 * Only for members: everything Smolit does is scoped to the person asking, and
 * a copilot that answers "you have no bookings" to somebody who is not signed
 * in is worse than one that is not there.
 */
function SmolitDock() {
  const { member } = useSession();
  const { open, openPanel } = useSmolit();
  const mobile = useIsMobileLayout();

  if (!member) return null;

  return (
    <>
      {/* No floating button on a phone: Smolit has a tab there, and the button
          would sit on top of it. */}
      {!open && !mobile ? <SmolitFab onClick={openPanel} /> : null}
      <SmolitPanel />
    </>
  );
}

function Shell() {
  const mobile = useIsMobileLayout();

  return (
    <div className={cn('min-h-screen bg-sand text-ink antialiased', mobile && 'pb-tab-bar')}>
      <Header />
      <Outlet />
      <Footer />
      <SmolitDock />
      {mobile ? <MobileTabs /> : null}
    </div>
  );
}

export function AppShell() {
  return (
    <SmolitProvider>
      <Shell />
    </SmolitProvider>
  );
}
